import type { MatchConfig } from '../sim/config';
import type { Placement } from '../sim/rules';
import type { Ball } from '../sim/physics';
// Room net client: join/create private rooms, shot-event sync.
// Server is authoritative on rules; we predict locally and reconcile at rest.
export interface ShotParams { chalkLevel?:number; miscue?:boolean; aim: number; power: number; tipX: number; tipY: number; vmax?: number; elevation?: number; calledBall?: number | null; calledPocket?: number | null }
export interface ServerBall {
  id: number; n: number | null; x: number; y: number; potted: boolean;
  z?: number; vx?: number; vy?: number; vz?: number;
  wx?: number; wy?: number; wz?: number; asleep?: boolean;
}

/** Exact server state seeds the next replay. Older snapshots describe resting balls. */
export function applyServerBalls(balls: Ball[], snapshots: ServerBall[]): void {
  for (const snapshot of snapshots) {
    const ball = balls.find(candidate => candidate.id === snapshot.id);
    if (!ball) continue;
    Object.assign(ball, snapshot, {
      z: snapshot.z ?? 0, vx: snapshot.vx ?? 0, vy: snapshot.vy ?? 0, vz: snapshot.vz ?? 0,
      wx: snapshot.wx ?? 0, wy: snapshot.wy ?? 0, wz: snapshot.wz ?? 0,
      asleep: snapshot.asleep ?? true,
    });
  }
}
export interface RoomState {
  chalk?: [number,number];
  return_order: number[];
  names?:string[]; ready?:boolean; busy?:boolean; registered?:boolean[];
  code: string; balls: ServerBall[]; current: number;
  groups: Array<string | null>; open: boolean; ball_in_hand: boolean;
  winner: number | null; message: string;
  break_shot: boolean; placement: Placement; kitchen_shot: boolean; rules: MatchConfig; revision: number;
}
export interface ShotEventsWire {
  first_contact: number | null; potted: number[]; off_table: Array<number | null>;
  rail_after_contact: boolean; cue_potted: boolean;
}

export class RoomClient {
  ws: WebSocket | null = null;
  seat: number | null = null;
  code = '';
  revision = 0;
  ready = false;
  private closed = false;
  private connectingTimer = 0;
  onState: (s: RoomState) => void = () => {};
  onShot: (by: number, shot: ShotParams) => void = () => {};
  onJoined: (names: string[]) => void = () => {};
  onError: (e: string) => void = () => {};
  onOpen: () => void = () => {};
  onClose: (message:string) => void = () => {};

  connect(): void {
    const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
    this.ws = new WebSocket(`${proto}//${location.host}/ws`);
    this.connectingTimer=window.setTimeout(()=>{this.close();this.onClose('Connection timed out. Try again.');},12000);
    this.ws.onopen = () => {clearTimeout(this.connectingTimer);this.onOpen();};
    this.ws.onmessage = (m) => {
      const d = JSON.parse(m.data);
      if (d.t === 'room') {
        this.seat = d.you;
        this.code = d.code;
        this.revision = d.state.revision;
        this.ready = d.state.ready ?? true;
        this.onState(d.state);
      } else if (d.t === 'state' || d.t === 'result') {
        this.revision = d.revision;
        this.ready = d.ready ?? true;
        this.onState(d);
      } else if (d.t === 'shot') {
        this.onShot(d.by, d.shot);
      } else if (d.t === 'joined') {
        this.onJoined(d.names);
      } else if(d.t==='left'){
        this.close();this.onClose(d.message??'Opponent left. Room closed.');
      } else if (d.t === 'error') {
        if(d.state){
          this.revision=d.state.revision;this.ready=d.state.ready??true;
          this.onState({...d.state,message:d.error});
        }
        this.onError(d.error);
      }
    };
    this.ws.onclose = () => {clearTimeout(this.connectingTimer);if(!this.closed){this.closed=true;this.onClose('Disconnected. Create or join a new room to continue.');}};
  }

  close():void {this.closed=true;clearTimeout(this.connectingTimer);this.ws?.close();this.ws=null;}
  send(o: object): void { if(this.ws?.readyState===WebSocket.OPEN)this.ws.send(JSON.stringify(o)); }
  create(name: string, rules: MatchConfig): void { this.send({ t: 'create', name, rules }); }
  join(code: string, name: string, chalkSim=false): void { this.send({ t: 'join', code, name, chalkSim }); }
  shot(s: ShotParams): void { this.send({ t: 'shot', shot: s, revision: this.revision }); }
  done(balls: ServerBall[], ev: ShotEventsWire): void { this.send({ t: 'done', balls, ev }); }
  chalk():void {this.send({t:'chalk',revision:this.revision});}
  place(x: number, y: number): void { this.send({ t: 'place', x, y, revision: this.revision }); }
}
